if (!window.Eurus.loadedScript.includes('product-cart.js')) {
  window.Eurus.loadedScript.push('product-cart.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      // Quantity-option driven limiter:
      // - When `Quantity` option is "Single product" or "Single tile", clamp qty to X.
      // - When `Quantity` option is "Box of 12" (or anything else), remove the clamp.
      // This is intentionally input-level (not cart-total) enforcement.
      window.Eurus = window.Eurus || {};
      if (!window.Eurus.quantityOptionLimiterInstalled) {
        window.Eurus.quantityOptionLimiterInstalled = true;

        const QUANTITY_OPTION_NAME = 'Quantity';
        const COLOR_OPTION_NAME = 'Color';
        const LIMIT_FALLBACK = 6;

        const isSingleQuantityValue = (value) => {
          if (!value) return false;
          const v = String(value).toLowerCase();
          return v.includes('single product') || v.includes('single tile');
        };

        const getFormSampleLimit = (qtyInput) => {
          const formEl = qtyInput?.form || (qtyInput?.getAttribute('form') ? document.getElementById(qtyInput.getAttribute('form')) : null);
          const raw = formEl?.dataset?.sampleLimit ?? formEl?.getAttribute?.('data-sample-limit');
          const parsed = parseInt(raw);
          return Number.isFinite(parsed) ? parsed : LIMIT_FALLBACK;
        };

        const getSelectedOptionValueForForm = (optionName, formId) => {
          // Prefer elements tied to the same form via `form="..."`
          const select = formId ? document.querySelector(`select[name="${optionName}"][form="${formId}"]`) : null;
          if (select) {
            const opt = select.selectedOptions && select.selectedOptions[0];
            if (opt) return (opt.textContent || opt.value || '').trim();
          }

          const radio = formId ? document.querySelector(`input[type="radio"][name="${optionName}"][form="${formId}"]:checked`) : null;
          if (radio) return radio.value;

          // Fallback: any matching inputs on the page (useful if only one form exists)
          const selectFallback = document.querySelector(`select[name="${optionName}"]`);
          if (selectFallback) {
            const opt = selectFallback.selectedOptions && selectFallback.selectedOptions[0];
            if (opt) return (opt.textContent || opt.value || '').trim();
          }
          const radioFallback = document.querySelector(`input[type="radio"][name="${optionName}"]:checked`);
          if (radioFallback) return radioFallback.value;

          return null;
        };

        const getAlpineData = (containerEl) => {
          if (!containerEl || !window.Alpine) return null;
          if (typeof window.Alpine.$data === 'function') {
            try {
              return window.Alpine.$data(containerEl);
            } catch (e) {
              // ignore
            }
          }
          return containerEl?._x_dataStack?.[0] || null;
        };

        const getProductQuantityInputs = () => {
          return Array.from(document.querySelectorAll('input[name="quantity"][type="number"]')).filter((input) => {
            const formEl = input?.form || (input?.getAttribute('form') ? document.getElementById(input.getAttribute('form')) : null);
            return formEl && formEl.getAttribute('data-type') === 'add-to-cart-form';
          });
        };

        const bindQuantityInputEvents = (qtyInput) => {
          if (qtyInput.dataset.eurusQuantityLimiterBound === '1') return;
          qtyInput.dataset.eurusQuantityLimiterBound = '1';

          qtyInput.addEventListener('input', () => {
            // If the limiter is applying programmatically, avoid warning spam.
            const silent = qtyInput.dataset.eurusQuantityLimiterProgrammatic === '1';
            if (silent) qtyInput.dataset.eurusQuantityLimiterProgrammatic = '0';
            clampQuantityInput(qtyInput, { showWarning: !silent });
          });

          qtyInput.addEventListener('blur', () => {
            clampQuantityInput(qtyInput, { showWarning: true });
          });
        };

        const clampQuantityInput = (qtyInput, { showWarning } = { showWarning: false }) => {
          if (!qtyInput) return;

          const formId = qtyInput.getAttribute('form') || qtyInput?.form?.id || null;
          const formEl = qtyInput?.form || (formId ? document.getElementById(formId) : null);
          if (!formEl || formEl.getAttribute('data-type') !== 'add-to-cart-form') return;

          const quantitySelected = getSelectedOptionValueForForm(QUANTITY_OPTION_NAME, formId);
          const shouldLimit = isSingleQuantityValue(quantitySelected);
          const limit = getFormSampleLimit(qtyInput);

          // Update qty + limiter state
          const containerEl = qtyInput.closest('.quantity-selector-container');
          const alpineData = getAlpineData(containerEl);
          const currentRaw = parseInt(qtyInput.value);
          const currentQty = Number.isFinite(currentRaw) ? currentRaw : 1;

          if (shouldLimit) {
            qtyInput.dataset.limit = String(limit);

            const clampedQty = Math.min(Math.max(1, currentQty), limit);
            const wasOverLimit = currentQty > limit;

            if (alpineData) {
              if ('hasSampleLimit' in alpineData) alpineData.hasSampleLimit = true;
              if ('sampleLimit' in alpineData) alpineData.sampleLimit = limit;
              if ('qty' in alpineData) alpineData.qty = clampedQty;
            }

            if (clampedQty !== currentQty) {
              qtyInput.value = String(clampedQty);
              // Keep Alpine calculator/price in sync when possible.
              qtyInput.dataset.eurusQuantityLimiterProgrammatic = '1';
              qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
            }

            if (wasOverLimit && showWarning && window.showSampleLimitWarning) {
              window.showSampleLimitWarning(limit);
            }
          } else {
            // Remove limit state
            qtyInput.removeAttribute('data-limit');
            if (alpineData) {
              if ('hasSampleLimit' in alpineData) alpineData.hasSampleLimit = false;
              if ('sampleLimit' in alpineData) alpineData.sampleLimit = null;
            }

            // If a sample-limit prompt is currently visible, remove it when limiting is no longer active.
            document.querySelectorAll('.prompt').forEach((p) => {
              const text = p?.textContent?.toLowerCase?.() || '';
              if (text.includes('limit online sample orders') || text.includes('sample orders')) {
                p.remove();
              }
            });
          }
        };

        const applyLimiterToAll = () => {
          const qtyInputs = getProductQuantityInputs();
          qtyInputs.forEach((qtyInput) => {
            bindQuantityInputEvents(qtyInput);
            clampQuantityInput(qtyInput, { showWarning: false });
          });
        };

        let scheduleToken = 0;
        const scheduleApply = () => {
          scheduleToken += 1;
          const token = scheduleToken;

          // Run multiple times because the theme re-renders option/quantity DOM asynchronously.
          [0, 80, 250, 600].forEach((delay) => {
            setTimeout(() => {
              if (token !== scheduleToken) return;
              applyLimiterToAll();
            }, delay);
          });
        };

        const bindOptionChangeListeners = () => {
          document.addEventListener('change', (e) => {
            const t = e.target;
            if (!t) return;
            if (
              t.matches(`select[name="${QUANTITY_OPTION_NAME}"]`) ||
              t.matches(`input[type="radio"][name="${QUANTITY_OPTION_NAME}"]`) ||
              t.matches(`select[name="${COLOR_OPTION_NAME}"]`) ||
              t.matches(`input[type="radio"][name="${COLOR_OPTION_NAME}"]`)
            ) {
              scheduleApply();
            }
          }, true);
        };

        bindOptionChangeListeners();
        // Initial clamp (after DOM settles).
        scheduleApply();
      }

      Alpine.data('xProductCart', (
        wrappringVariantId,
        engravingVariantId,
      ) => ({
        loading: false,
        errorMessage: false,
        mainHasError: false,
        buttonSubmit: "",
        error_message_wrapper: {},
        stopAction: false,
        insuranceVariantId: '',
        checkSampleLimitBeforeAddToCart(e, required, quickView, sticky) {
          // Prevent the default form submission
          e.preventDefault();
          e.stopPropagation();
          
          // Also prevent immediate propagation
          e.stopImmediatePropagation();
          
          // Check if this is a sample product and quantity exceeds limit
          let productForm = this.$el.closest('.product-info') || this.$el.closest('form');
          if (!productForm) {
            // Fallback: look for the form in the document
            productForm = document.querySelector('form[data-type="add-to-cart-form"]');
          }
          
          if (productForm) {
            // Try multiple selectors to find the quantity input
            let qtyInput = productForm.querySelector('input[name="quantity"]');
            if (!qtyInput) {
              qtyInput = productForm.querySelector('input[id*="Quantity"]');
            }
            if (!qtyInput) {
              qtyInput = productForm.querySelector('input[type="number"]');
            }
            
            if (qtyInput && qtyInput.dataset.limit) {
              const limit = parseInt(qtyInput.dataset.limit);
              const currentQty = parseInt(qtyInput.value);
              
              // Check if selected variant is a sample
              const checkedInputs = productForm.querySelectorAll('input[type="radio"]:checked');
              let isSample = false;
              
              // First check radio inputs (variant options)
              for (let input of checkedInputs) {
                const optionValue = input.value.toLowerCase();
                if (optionValue.includes('single tile') || 
                    optionValue.includes('sample') || 
                    optionValue.includes('single sheet')) {
                  isSample = true;
                  break;
                }
              }
              
              // If no sample detected via radio, check if the product itself is a sample
              if (!isSample) {
                // Check if the product title or handle indicates it's a sample
                const productTitle = document.querySelector('.product-single__title')?.textContent?.toLowerCase() || '';
                const productHandle = window.location.pathname.toLowerCase();
                
                if (productTitle.includes('sample') || 
                    productHandle.includes('sample') ||
                    productTitle.includes('single tile') ||
                    productHandle.includes('single-tile')) {
                  isSample = true;
                }
              }
              
              if (isSample) {
                // Determine currently selected variant ID (use the current form value)
                const selectedVariantIdInput = productForm.querySelector('input[name="id"]');
                const selectedVariantId = selectedVariantIdInput ? parseInt(selectedVariantIdInput.value, 10) : null;

                // Check cart totals before allowing add to cart (per-variant sample limit)
                this._checkCartSampleLimit(limit, currentQty, selectedVariantId, () => {
                  // Cart check passed, proceed with add to cart
                  this.addToCart(e, required, quickView, sticky);
                }, () => {
                  // Cart check failed, show warning and prevent
                  if (window.showSampleLimitWarning) {
                    window.showSampleLimitWarning(limit);
                  } else {
                    setTimeout(() => {
                      if (window.showSampleLimitWarning) {
                        window.showSampleLimitWarning(limit);
                      } else {
                        this._showSampleLimitWarningFallback(limit);
                      }
                    }, 100);
                  }
                  
                  // Reset loading state
                  this.loading = false;
                  
                  // Disable submit button temporarily
                  if (productForm) {
                    const submitBtn = productForm.querySelector('button[type="submit"]');
                    if (submitBtn) {
                      submitBtn.disabled = true;
                      setTimeout(() => {
                        submitBtn.disabled = false;
                      }, 1000);
                    }
                  }
                });
                return; // Don't proceed yet, wait for cart check
              }
            }
          }
          
          // If no sample limit issue, proceed with normal add to cart
          this.addToCart(e, required, quickView, sticky);
        },
        
        _showSampleLimitWarningFallback(limit) {
          // Remove any existing prompts
          const oldPrompts = document.querySelectorAll('.prompt');
          oldPrompts.forEach(prompt => {
            prompt.remove();
          });
          
          // Show warning below the quantity selector on product page
          let quantityContainer = document.querySelector('.quantity-selector-container') ||
                                 document.querySelector('input[id*="Quantity"]')?.closest('.inline-flex') ||
                                 document.querySelector('input[name="quantity"]')?.closest('.inline-flex') ||
                                 document.querySelector('[x-data*="qty:"]') ||
                                 document.querySelector('.quantity-selector');
          
          if (quantityContainer) {
            // Insert warning after the quantity selector
            const warningMessage = `<div class="prompt"><span class="close">&times;</span>Please limit online sample orders to a maximum of ${limit} pieces. For special requests, reach out to us at (856) 988-1802.</div>`;
            
            quantityContainer.insertAdjacentHTML('afterend', warningMessage);
            
            // Add close button functionality
            const closeButton = document.querySelector('.prompt .close');
            if (closeButton) {
              closeButton.addEventListener('click', function() {
                const prompt = this.closest('.prompt');
                if (prompt) {
                  prompt.remove();
                }
              });
            }
          } else {
            // Fallback to product container if quantity selector not found
            let productContainer = document.querySelector('.product-single__meta') || 
                                  document.querySelector('.product-info') || 
                                  document.querySelector('form[data-type="add-to-cart-form"]');
            
            if (productContainer) {
              const warningMessage = `<div class="prompt"><span class="close">&times;</span>Please limit online sample orders to a maximum of ${limit} pieces. For special requests, reach out to us at (856) 988-1802.</div>`;
              
              productContainer.insertAdjacentHTML('afterbegin', warningMessage);
              
              // Add close button functionality
              const closeButton = productContainer.querySelector('.prompt .close');
              if (closeButton) {
                closeButton.addEventListener('click', function() {
                  const prompt = this.closest('.prompt');
                  if (prompt) {
                    prompt.remove();
                  }
                });
              }
            }
          }
        },
        
        // Check cart for existing sample quantities before allowing add to cart
        _checkCartSampleLimit(limit, newQty, variantId, onSuccess, onFailure) {
          // Fetch current cart data
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              let sampleQty = 0;

              // If we have a variantId, limit per variant; otherwise fallback to global sample total.
              if (variantId) {
                cart.items.forEach(item => {
                  if (this._isCartItemSample(item) && item.variant_id === variantId) {
                    sampleQty += item.quantity;
                  }
                });
              } else {
                cart.items.forEach(item => {
                  if (this._isCartItemSample(item)) {
                    sampleQty += item.quantity;
                  }
                });
              }

              // Check if adding new quantity would exceed limit for this variant or global sample set
              if (sampleQty + newQty > limit) {
                onFailure();
              } else {
                onSuccess();
              }
            })
            .catch(error => {
              // If cart fetch fails, proceed with add to cart (fail-safe)
              onSuccess();
            });
        },
        
        // Check if a cart item is a sample product
        _isCartItemSample(cartItem) {
          // Check variant title
          if (cartItem.variant_title) {
            const variantTitle = cartItem.variant_title.toLowerCase();
            if (variantTitle.includes('single tile') || 
                variantTitle.includes('sample') || 
                variantTitle.includes('single sheet')) {
              return true;
            }
          }
          
          // Check product title
          if (cartItem.product_title) {
            const productTitle = cartItem.product_title.toLowerCase();
            if (productTitle.includes('sample') || 
                productTitle.includes('single tile')) {
              return true;
            }
          }
          
          // Check product handle
          if (cartItem.handle) {
            const productHandle = cartItem.handle.toLowerCase();
            if (productHandle.includes('sample') || 
                productHandle.includes('single-tile')) {
              return true;
            }
          }
          
          return false;
        },

        loadInsurance(id) {
          if (this.insuranceVariantId == '') {
            this.insuranceVariantId = id;
          }
        },
        scrollToAtcBtn(btnId) {
          const originalAtcBtn = document.querySelector(`#${btnId}`);
          originalAtcBtn.scrollIntoView({
            behavior: "smooth",
            block: 'center',
            inline: 'center'
          })
        },
        async hashProperties(formData) {
          let result = [];
          for (let [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) {
              if (value instanceof File) {
                result.push(`${value.name}:${value.size}:${value.type}`);
              } else {
                result.push(value);
              }
            }
          }
          result.push(formData.get('id'));
          result.sort();

          let data = result.join('|');

          const encoder = new TextEncoder();
          const dataBuffer = encoder.encode(data);

          const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

          return hashHex;
        },
        async addToCart(e, required, quickView, sticky) {
          this.loading = true;
          e.preventDefault();

          setTimeout(async () => {
            if (required) {
              var productInfo = this.$el.closest('.product-info');
              if(sticky){
                productInfo = document.querySelector('.product-info');
              }
              if (productInfo) {
                var propertiesInput = productInfo.querySelectorAll(`.customization-picker`);
                this.stopAction = false;
                let scrollStatus = false;
                
                propertiesInput.length && propertiesInput.forEach((input) => {
                  if (input.required && input.value.trim() == '' || input.classList.contains("validate-checkbox")) {
                    input.classList.add("required-picker");
                    this.stopAction = true;
                    if(!scrollStatus){
                      input.parentElement.querySelector('.text-required').scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                      scrollStatus = true;
                    }    
                  }                
                });              
              }
              if (this.stopAction) {
                setTimeout (() => {
                  this.loading = false;
                }, 500);
                return true;
              }
            }

            if (this.$refs.engraving_text && engravingVariantId) {
              if (this.$refs.engraving_text.value.trim()) {
                if (!this.$refs.engraving_text.hasAttribute('name')) this.$refs.engraving_text.setAttribute('name', this.$refs.text_area_name.value);
              } else {
                if (this.$refs.engraving_text.hasAttribute('name')) this.$refs.engraving_text.removeAttribute('name');
              }
            }

            var productForm = this.$el.closest('.product-info') || this.$el.closest('form');
            var edt_element = productForm ? productForm.querySelector('.hidden.cart-edt-properties') : null;
            if (edt_element) {
              edt_element.value = edt_element.value.replace("time_to_cut_off", Alpine.store('xEstimateDelivery').noti)
            }
            let formData = new FormData(this.$refs.product_form);
            
            // Check if calculator is present and use calculated quantity
            const calculatorWrapper = document.querySelector('.calculator-wrapper.active');
            let calculatedQuantity = null;
            if (calculatorWrapper) {
              const finalQuantityInput = calculatorWrapper.querySelector('#finalquantity');
              if (finalQuantityInput && finalQuantityInput.value && parseInt(finalQuantityInput.value) > 0) {
                calculatedQuantity = finalQuantityInput.value;
                // Update the quantity in the form data
                formData.set('quantity', calculatedQuantity);
              }
            }

            if (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId && this.$refs.gift_wrapping_id) {              
              await this.hashProperties(formData).then(hashHex => {
                this.$refs.gift_wrapping_id.value = hashHex;
                formData = new FormData(this.$refs.product_form);
              });
            }
            
            // Re-apply calculated quantity after gift wrapping hash (if needed)
            if (calculatedQuantity) {
              formData.set('quantity', calculatedQuantity);
            }

            formData.append(
              'sections',
              Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            const newFormData = new FormData();
            for (let [key, value] of formData.entries()) {
              if (value !== '') {
                newFormData.append(key, value);
              }
            }
            await fetch(`${Eurus.cart_add_url}`, {
              method:'POST',
              headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
              body: newFormData
            }).then(reponse => {
              return reponse.json();
            }).then(async (response) => {
              if (response.status == '422') {
                if (typeof response.errors == 'object') {
                  this.error_message_wrapper = response.errors;
                  document.querySelector('.recipient-error-message').classList.remove('hidden');
                } else {
                  this.errorMessage = true;
                  setTimeout(() => {
                    this.errorMessage = false;
                  }, 3000);
                  if(this.$refs.error_message){
                    this.$refs.error_message.textContent = response.description;
                  }
                  if(this.$refs.error_message_mobile){
                    this.$refs.error_message_mobile.textContent = response.description;
                  }
                }
                if (Alpine.store('xMiniCart')) {
                  Alpine.store('xMiniCart').reLoad();
                }
              } else {  
                if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
                  Alpine.store('xCartNoti').setItem(response); 
                }
                let match = document.cookie.match('(^|;)\\s*' + 'eurus_insurance' + '\\s*=\\s*([^;]+)');
                if (
                  (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId) || 
                  (this.$refs.engraving_text && engravingVariantId && this.$refs.engraving_text.value.trim()) || 
                  (this.insuranceVariantId && !localStorage.getItem('insuranceRemoved') && (!match || match[1].trim() === ''))
                ) {
                  let additionalOptionData = {
                    items: [],
                    sections:  Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
                  };
                  if (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId && response.properties['_gift_wrapping_id']) {
                    additionalOptionData.items.push(
                      {
                        id: wrappringVariantId,
                        quantity: 1,
                        properties: {
                          "For": response.title,
                          "_key_link": response.properties['_gift_wrapping_id']
                        }
                      }
                    );
                  }
                  if (this.$refs.engraving_text && engravingVariantId && this.$refs.engraving_text.value.trim()) {
                    additionalOptionData.items.push(
                      {
                        id: engravingVariantId,
                        quantity: 1
                      }
                    );
                  }
                  if (this.insuranceVariantId && !localStorage.getItem('insuranceRemoved')) {
                    let match = document.cookie.match('(^|;)\\s*' + 'eurus_insurance' + '\\s*=\\s*([^;]+)');
                    if (!match || match[1].trim() === '') {
                      additionalOptionData.items.push(
                        {
                          id: this.insuranceVariantId,
                          quantity: 1
                        }
                      );
                    }
                  }

                  if (additionalOptionData.items.length !== 0) {
                    await window.fetch('/cart/add.js', {
                      method: 'POST',
                      credentials: 'same-origin',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                      },
                      body: JSON.stringify(additionalOptionData),
                    }).then((response) => {
                      return response.json();
                    }).then(response => {
                      document.querySelector('.recipient-error-message') ? document.querySelector('.recipient-error-message').classList.add('hidden') : '';
                      this.error_message_wrapper = {};
        
                      if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
                        Alpine.store('xQuickView').show = false;
                      }
                      Alpine.store('xPopup').close();
                       
                      if((quickView && Alpine.store('xQuickView').buttonQuickView && Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                        document.dispatchEvent(new CustomEvent("eurus:cart:add-as-bundle"));
                      } else {
                        Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                          const sectionElement = document.querySelector(section.selector);
                          if (sectionElement) {
                            if (response.sections[section.id])
                              sectionElement.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
                          }
                        }));
                        if (!Alpine.store('xCartNoti') || !Alpine.store('xCartNoti').enable) {
                          setTimeout(() => {
                            Alpine.store('xMiniCart').openCart();
                          }, 500);
                          document.dispatchEvent(new CustomEvent("eurus:cart:redirect"));  
                        }  
                        Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span').innerHTML);
                        document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));

                        // Dispatch box:added event for sealer popup (only for non-excluded products)
                        this.dispatchBoxAddedEvent(response);
                      }
                    });  
                  }
                } else {
                  document.querySelector('.recipient-error-message') ? document.querySelector('.recipient-error-message').classList.add('hidden') : '';
                  this.error_message_wrapper = {};
        
                  if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
                    Alpine.store('xQuickView').show = false;
                  }
                  Alpine.store('xPopup').close();
                  
                  if((quickView && Alpine.store('xQuickView').buttonQuickView && Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                    document.dispatchEvent(new CustomEvent("eurus:cart:add-as-bundle"));
                  } else {
                    Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                      const sectionElement = document.querySelector(section.selector);
                      if (sectionElement) {
                        if (response.sections[section.id])
                          sectionElement.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
                      }
                    }));
                    if (!Alpine.store('xCartNoti') || !Alpine.store('xCartNoti').enable) {
                      setTimeout(() => {
                        Alpine.store('xMiniCart').openCart();
                      }, 500);
                    }               
                    Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span').innerHTML);
                    document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
                    document.dispatchEvent(new CustomEvent("eurus:cart:redirect"));

                    // Dispatch box:added event for sealer popup (only for non-excluded products)
                    this.dispatchBoxAddedEvent(response);
                  }
                }
              }
            }).catch((error) => {
              console.error('Error:', error);
            }).finally(() => {
              this.loading = false;
              if ((quickView && Alpine.store('xQuickView').buttonQuickView && !Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && !this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                if(this.$refs.gift_wrapping_checkbox) this.$refs.gift_wrapping_checkbox.checked = false;
                if(this.$refs.gift_wrapping_id) this.$refs.gift_wrapping_id.value = '';
              }
              document.cookie = `eurus_insurance=${this.insuranceVariantId}; path=/`;  
            })
          }, 0)
        },
        dispatchBoxAddedEvent(response) {
          // Check if the added product should be excluded from sealer popup
          if (this.isExcludedFromSealerPopup(response)) {
            return; // Don't dispatch event for excluded products
          }
          
          // Dispatch the box:added event after a small delay to ensure DOM updates are complete
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent("box:added", {
              detail: {
                product: response,
                timestamp: Date.now()
              }
            }));
          }, 100);
        },
        isExcludedFromSealerPopup(response) {
          // List of excluded product variants (case-insensitive matching)
          const excludedVariants = [
            'single tile',
            'sample',
            'sample o',
            'sample x',
            'sample (2 pcs)',
            'sample (4 pcs)',
            'single sheet',
            'single sheet (1 sqft)',
            'full sheet 1sqft',
            'sample 8x8'
          ];
          
          // Check variant title
          if (response.variant_title) {
            const variantTitle = response.variant_title.toLowerCase();
            for (const excluded of excludedVariants) {
              if (variantTitle.includes(excluded)) {
                return true;
              }
            }
          }
          
          // Check product title
          if (response.title || response.product_title) {
            const productTitle = (response.title || response.product_title).toLowerCase();
            for (const excluded of excludedVariants) {
              if (productTitle.includes(excluded)) {
                return true;
              }
            }
          }
          
          return false;
        }
      }))
    });
  });
}
